<template>
    <!-- Greenhouse Year Space -->
    <v-hover>
        <template v-slot="{ hover }">
            <v-card flat :loading="isLoading">
                <slot name="header"></slot>
                <div class="px-0 pt-0">
                    <v-row align="center" class="mx-0">
                        <v-col cols="12" class="py-0">
                            <div id="chart">
                                <apexchart
                                    type="bar"
                                    height="100"
                                    :options="chartOptions"
                                    :series="series"
                                ></apexchart>
                            </div>
                        </v-col>
                    </v-row>
                </div>

                <slot name="footer"></slot>
            </v-card>
        </template>
    </v-hover>
</template>

<script>
import gql from "graphql-tag";
import VueApexCharts from "vue-apexcharts";

export default {
    name: "greeenhouse-planned-finance-chart",
    components: { apexchart: VueApexCharts },
    // props: ["greenhouseNodeId", "taskYear"],
    data() {
        return {
            isLoading: false,

            series: [],
            chartOptions: {
                dataLabels: {
                    enabled: true,
                    textAnchor: "left",
                    formatter: function(_val, opt) {
                        let series = opt.w.config.series;
                        let idx = opt.dataPointIndex;
                        const total = series.reduce(
                            (total, self) => total + self.data[idx],
                            0
                        );

                        // apexcharts doesn't show 0 value, so show the data label on last data with value > 0
                        const rest_total = series
                            .slice(opt.seriesIndex + 1)
                            .reduce((total, self) => total + self.data[idx], 0);

                        return rest_total > 0
                            ? ""
                            : "€" + total.toLocaleString();
                    },
                    style: {
                        colors: ["#000"]
                    }
                },
                chart: {
                    id: "greenhouse-finance-chart",
                    type: "bar",
                    height: 100,
                    stacked: true,
                    toolbar: {
                        show: false
                    },
                    zoom: {
                        enabled: false
                    }
                },
                grid: {
                    xaxis: {
                        lines: {
                            show: false
                        }
                    },
                    yaxis: {
                        lines: {
                            show: false
                        }
                    }
                },
                colors: [
                    "#E71D36",
                    "#EF6B7B",
                    "#F17E8C",
                    "#F3919C",
                    "#F5A3AD",
                    "#00E396"
                ],
                plotOptions: {
                    bar: {
                        horizontal: true,
                        dataLabels: {
                            position: "right"
                        }
                    }
                },
                stroke: {
                    width: 1,
                    colors: ["#fff"]
                },
                xaxis: {
                    categories: ["Cost", "Revenue"],
                    labels: {
                        show: false,
                        formatter: function(val) {
                            return "€" + val.toFixed(0).toLocaleString();
                        }
                    },
                    axisBorder: {
                        show: false
                    }
                },
                yaxis: {
                    title: {
                        text: undefined
                    }
                },
                tooltip: {
                    y: {
                        formatter: function(val) {
                            return "€" + val.toFixed(0).toLocaleString();
                        }
                    }
                },
                fill: {
                    opacity: 1
                },
                legend: {
                    show: false
                }
            }
        };
    },
    computed: {},
    watch: {},
    methods: {
        getGreenhousePlannedFinance(greenhouseNodeId, year, folderID, nullFilter) {
            // console.log(this.greenhouseNodeId, this.year);

            if (!greenhouseNodeId || !year) {
                return;
            }

            this.isLoading = true;

            this.$apollo
                .query({
                    // Query
                    query: gql`
                        query greenhouse($greenhouse_Id: ID!, $year: Int!, $planFolder: ID,  $planFolderIsnull: Boolean) {
                            greenhouse(id: $greenhouse_Id) {
                                id
                                name
                                greenhousePlannedFinanceSet(year: $year, planFolder: $planFolder, planFolder_Isnull: $planFolderIsnull) {
                                    edges {
                                        node {
                                            id
                                            youngPlantCost
                                            growthCost
                                            productCost
                                            profitableBusinessCost
                                            totalBusinessCost
                                            totalRevenue
                                            totalProfit
                                        }
                                    }
                                }
                            }
                        }
                    `,
                    // Parameters
                    variables: {
                        greenhouse_Id: greenhouseNodeId,
                        year: year,
                        planFolder: folderID,
                        planFolderIsnull: nullFilter
                    },
                    fetchPolicy: "network-only"
                })
                .then(response => {
                    // Result
                    // console.log(response.data);

                    if (
                        response.data.greenhouse &&
                        response.data.greenhouse.greenhousePlannedFinanceSet
                            .edges.length > 0
                    ) {
                        const result =
                            response.data.greenhouse.greenhousePlannedFinanceSet
                                .edges[0];

                        const _youngPlantCost = Math.round(
                            result.node.youngPlantCost
                        );
                        const _growthCost = Math.round(result.node.growthCost);
                        const _productCost = Math.round(
                            result.node.productCost
                        );
                        const _profitableBusinessCost = Math.round(
                            result.node.profitableBusinessCost
                        );
                        const _totalBusinessCost = Math.round(
                            result.node.totalBusinessCost
                        );
                        const _nonProfitableBusinessCost =
                            _totalBusinessCost - _profitableBusinessCost;
                        const _totalRevenue = Math.round(
                            result.node.totalRevenue
                        );
                        // const _totalProfit = Math.round(result.node.totalProfit);

                        this.series = [
                            {
                                name: "Non-profitable Business Cost",
                                data: [_nonProfitableBusinessCost, 0]
                            },
                            {
                                name: "Profitable Business Cost",
                                data: [_profitableBusinessCost, 0]
                            },
                            {
                                name: "Young Plant Cost",
                                data: [_youngPlantCost, 0]
                            },
                            {
                                name: "Growth Cost",
                                data: [_growthCost, 0]
                            },
                            {
                                name: "Product Cost",
                                data: [_productCost, 0]
                            },
                            {
                                name: "Revenue",
                                data: [0, _totalRevenue]
                            }
                        ];
                    } else {
                        this.series = [];
                    }
                })
                .catch(error => {
                    // Error
                    console.error(error);
                })
                .finally(() => {
                    // this.isSavingOrderPrintStatus = false;
                    this.isLoading = false;
                });
        }
    }
};
</script>